import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { reportClientDiagnostic } from "@/lib/clientDiagnostics";
import { EffectRenderer } from "./effects/EffectRenderer";

interface AnimatedLogoProps {
  src: string; // Logo URL (custom SVG or default)
  alt: string;
  className?: string;
  animationEffect?: string | null; // Effect to apply
  /**
   * Raw SVG markup for the default logo. When provided it renders directly in
   * the page — no separate image request — so devices with unreliable asset
   * fetching (in-car browsers) can never lose the logo to a failed load.
   * Scheduled logos from storage still arrive via `src`.
   */
  inlineSvg?: string;
}

/**
 * AnimatedLogo
 *
 * Wrapper component for the header logo that provides:
 * - Smooth fade transitions when logo changes (400ms crossfade)
 * - Conditional rendering of animation effects
 * - Accessibility support (respects prefers-reduced-motion)
 *
 * @param src - Logo image URL
 * @param alt - Alt text for logo
 * @param className - Optional CSS classes for the logo image
 * @param animationEffect - Effect name to render (from database)
 */
export const AnimatedLogo: React.FC<AnimatedLogoProps> = ({
  src,
  alt,
  className = "",
  animationEffect = null,
  inlineSvg,
}) => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = () => setPrefersReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Reset error state when src changes
  useEffect(() => {
    setImageError(false);
  }, [src]);

  // Telemetry only — behavior is unchanged (error still swaps in the text
  // fallback immediately). A failed <img> load isn't a JS error, so Sentry
  // never sees it on its own; this report is how we diagnose the Tesla
  // browser dropping the logo. Reported through TWO channels because the
  // Tesla case showed the failing connection can also eat the Sentry
  // delivery: the client-diagnostics channel queues in localStorage and
  // flushes on a later load.
  const reportLogoFailure = async () => {
    const details: Record<string, unknown> = {
      src,
      online: typeof navigator !== "undefined" ? navigator.onLine : "unknown",
      visibility: typeof document !== "undefined" ? document.visibilityState : "unknown",
      pageAgeMs: Math.round(performance.now()),
      swControlled:
        typeof navigator !== "undefined" && "serviceWorker" in navigator
          ? Boolean(navigator.serviceWorker.controller)
          : "unsupported",
    };
    // Probe: re-request the failed URL and record what actually comes back.
    // Distinguishes a network drop (probe throws) from an intercepted or
    // rewritten response (wrong status/content-type/size) — the Tesla fails
    // this one asset ~1.5s into an otherwise successful page load, and the
    // img element's error event says nothing about why.
    try {
      const resp = await fetch(src, { cache: "no-store" });
      const blob = await resp.blob();
      details.probe = {
        status: resp.status,
        contentType: resp.headers.get("content-type"),
        bytes: blob.size,
      };
    } catch (err) {
      details.probeError = String(err);
    }
    Sentry.captureMessage("Header logo image failed to load", {
      level: "warning",
      tags: { surface: "header-logo" },
      extra: details,
    });
    reportClientDiagnostic("header-logo", details);
  };

  // Self-report whenever the text fallback actually RENDERS. The failure
  // beacon above fires on the img error event; this one proves what the user
  // ended up seeing, and from which render branch — so "broken logo but no
  // diagnostics row" can only mean the device is running a build older than
  // this code (stale cache), never a silent failure in this code.
  useEffect(() => {
    if (!imageError || inlineSvg) return;
    reportClientDiagnostic("header-logo", {
      phase: "fallback-rendered",
      src,
      hasInlineSvg: false,
      pageAgeMs: Math.round(performance.now()),
    });
  }, [imageError, inlineSvg, src]);

  // If user prefers reduced motion, skip animations
  const shouldAnimate = !prefersReducedMotion;

  return (
    <div className="relative inline-block pointer-events-none" role="img" aria-label={alt} aria-live="polite">
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={src} // Key change triggers re-render for crossfade
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: shouldAnimate ? 0 : 1 }}
          transition={{
            duration: 0,
            delay: 0,
          }}
          style={{ willChange: "opacity" }}
          className="relative"
        >
          {inlineSvg ? (
            <>
              {/* The className sets the height. The svg fills the box and
                  preserveAspectRatio (injected below) letterboxes the artwork
                  left-aligned — replicating the old img's max-width:100% +
                  object-contain object-left, which do NOT apply to inline
                  svg. Without this the svg asserts its natural ~13:1 width
                  and overflows narrow viewports (seen on the Tesla). */}
              <div
                className={`${className} pointer-events-none max-w-full [&>svg]:h-full [&>svg]:w-full`}
                dangerouslySetInnerHTML={{
                  __html: inlineSvg.replace(/<svg /, '<svg preserveAspectRatio="xMinYMid meet" '),
                }}
              />
              {animationEffect && (
                <EffectRenderer effect={animationEffect} className="absolute inset-0 pointer-events-none" />
              )}
            </>
          ) : imageError ? (
            /* Text fallback when SVG fails to load */
            <div 
              className={`${className} pointer-events-none text-white`}
              style={{
                fontFamily: '"Goudy Old Style", "Palatino", "Book Antiqua", "Times New Roman", serif',
                fontSize: 'clamp(2rem, 4vw, 3.5rem)',
                fontWeight: 'normal',
                letterSpacing: '0.03em',
                lineHeight: '1.2',
                whiteSpace: 'nowrap'
              }}
            >
              {alt}
            </div>
          ) : (
            <>
              {/* Logo image - decorative, should not block pointer events (so mobile controls remain clickable) */}
              <img
                src={src}
                alt=""
                role="presentation"
                className={`${className} pointer-events-none`}
                onError={() => {
                  reportLogoFailure();
                  setImageError(true);
                }}
              />

              {/* Conditional effect overlay */}
              {animationEffect && (
                <EffectRenderer effect={animationEffect} className="absolute inset-0 pointer-events-none" />
              )}
            </>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
